import * as React from "react";
import { connect } from "react-redux";
import { getCustomOperationTitle } from "../../../../lib/Util";
import MaterialTitle from "./MaterialTitle";
import DraggableOrder from "./DraggableOrder";
import { LINE_GROUP_TAGS_ACCESS } from "../../../../lib/ManufacturingTags.generated";
import * as r from "../../../../lib/backend/reports.generated.types";
import { getLineGroupTags } from "../../../../lib/BusinessLogic";
import PerformanceAnalysis from "../../../react/performance/PerformanceAnalysis";
import ProcessNumRect from "./ProcessNumRect";
import CollectiveOrderBadge from "./CollectiveOrderBadge";
import OrderTooltip from "./OrderTooltip";
import OrderRectangle from "./OrderRectangle";
import { getCursorValue } from "../reducers/properties";

import type { MaterialTitleProps } from "./MaterialTitle";
import type { ReduxState } from "../reducers/reducers";
import type { IOrderProducedModelEx } from "../reducers/linesOrders";
import type { OrderTypeEnum } from "../../../../lib/ManufacturingConsts.generated";
import type { ITags } from "../../../../lib/Models";
import type { OwnProps as OrderRectangleProps } from "./OrderRectangle";

import "./Order.scss";

type OwnProps = {
    order_uuid: string;
    line_uuid: string;
    order_index: number;
    order_sim_index: number;
    line_index: number;
    order_rectangle_attributes: OrderRectangleProps;
    material_title_attributes: MaterialTitleProps;
    order_color: string;
    material_title: string;
    last_shift_number: number;
    is_parallel_line: boolean;
    is_extra_line?: boolean;
    sim_ignore_earliest_start: boolean;
    material_external_id: string;
    order_process_num: string;
    order_type: OrderTypeEnum;
    line_order?: IOrderProducedModelEx;
    disabled_shifts: r.ISimulationReportOrderLineShiftFeatureItem[];
    is_linked_operation: boolean;
    is_historical: boolean;
};

type Props = OwnProps & ReduxProps;

type State = {
    comment: string;
};

class Order extends React.Component<Props, State> {
    performance_key: string | null = null;

    state: State = {
        comment: "",
    };

    constructor(props: Props) {
        super(props);
        this.performance_key = "order-" + props.order_uuid;
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prev_props: Props) {
        PerformanceAnalysis.componentDidUpdate(this.performance_key, prev_props, this.props, Date.now());
        if (
            prev_props.show_edit_comment !== this.props.show_edit_comment ||
            (prev_props.line_order && prev_props.line_order.order_comment !== this.state.comment)
        ) {
            this.load();
        }
    }

    load() {
        if (this.props.line_order) {
            this.setState({
                comment: this.props.line_order.order_comment,
            });
        }
    }

    renderOrderContent() {
        PerformanceAnalysis.render(this.performance_key, Date.now());
        const { order, insight_tooltips, unscheduled_order } = this.props.order_rectangle_attributes;

        const { line_order, base_order, material } = this.props;
        const line_group_tags = getLineGroupTags(this.props.line_group_uuid);
        const operation_title_template =
            LINE_GROUP_TAGS_ACCESS.gantt_chart_custom_operation_title_template(line_group_tags);
        const order_model = line_order || base_order;
        const tags: ITags = order_model ? order_model.tags : {};
        const custom_title =
            order_model !== undefined && material !== undefined
                ? getCustomOperationTitle(operation_title_template, order_model, material)
                : "";
        const tooltip = (
            <OrderTooltip
                order={order}
                unscheduled_order={unscheduled_order}
                insight_tooltips={insight_tooltips}
                properties={[]}
                material={this.props.material}
                tags={tags}
                custom_title={custom_title}
                is_historical={this.props.is_historical}
                from_shift_time={this.props.from_shift_time}
                original_earliest_start={this.props.original_earliest_start}
                original_earliest_end={this.props.original_earliest_end}
                original_initial_earliest_start={this.props.original_initial_earliest_start}
                original_initial_earliest_end={this.props.original_initial_earliest_end}
            />
        );
        const is_rescheduled = this.props.is_rescheduled;
        const is_ctrl_down = this.props.is_ctrl_down;

        const cursor = getCursorValue({
            is_ctrl_down: this.props.is_ctrl_down,
            is_rescheduled: this.props.is_rescheduled,
            source_type: this.props.source_type,
            is_multiselected: this.props.is_multiselected,
        });

        return (
            <g>
                <OrderRectangle
                    key={this.props.order_uuid}
                    {...this.props.order_rectangle_attributes}
                    tooltip={tooltip}
                    material_title={this.props.material_title_attributes.material_title}
                    disabled_shifts={this.props.disabled_shifts}
                    order_uuid={this.props.order_uuid}
                    is_multiselected={this.props.is_multiselected}
                    cursor={cursor}
                />
                <MaterialTitle
                    key={"order-material-title-" + this.props.order_uuid + custom_title}
                    {...this.props.material_title_attributes}
                    tooltip={tooltip}
                    is_rescheduled={is_rescheduled}
                    is_ctrl_down={is_ctrl_down}
                    custom_title={custom_title}
                    cursor={cursor}
                />
                <ProcessNumRect
                    process_num={this.props.order_process_num}
                    width={this.props.order_rectangle_attributes.width}
                    height={this.props.order_rectangle_attributes.height}
                    x={this.props.order_rectangle_attributes.x}
                    y={this.props.order_rectangle_attributes.y}
                    order_external_id={order.order_external_id}
                    order_uuid={this.props.order_uuid}
                />
                <CollectiveOrderBadge
                    width={this.props.order_rectangle_attributes.width}
                    height={this.props.order_rectangle_attributes.height}
                    x={this.props.order_rectangle_attributes.x}
                    y={this.props.order_rectangle_attributes.y}
                    order_uuid={this.props.order_uuid}
                    is_unscheduled={false}
                />
            </g>
        );
    }

    render() {
        const { order } = this.props.order_rectangle_attributes;

        return (
            <DraggableOrder
                key={"order_uuid_" + this.props.order_uuid + " " + this.props.order_index}
                order_external_id={order.order_external_id}
                order_uuid={this.props.order_uuid}
                order_type={this.props.order_type}
                line_index={this.props.line_index}
                order_index={this.props.order_index}
                order_sim_index={this.props.order_sim_index}
                order_color={this.props.order_color}
                line_uuid={this.props.line_uuid}
                last_shift_number={this.props.last_shift_number}
                x={this.props.order_rectangle_attributes.x}
                y={this.props.order_rectangle_attributes.y}
                width={this.props.order_rectangle_attributes.width}
                height={this.props.order_rectangle_attributes.height}
                production_version={this.props.order_rectangle_attributes.production_version}
                is_parallel_line={this.props.is_parallel_line}
                order_process_num={this.props.order_process_num}
                opacity={1}
                earliest_start={new Date(order.earliest_start).getTime()}
                earliest_end={this.props.order_rectangle_attributes.earliest_end}
                earliest-end={this.props.order_rectangle_attributes.earliest_end}
                source_type={this.props.source_type}
                is_extra_line={this.props.is_extra_line}
                sim_ignore_earliest_start={this.props.sim_ignore_earliest_start}
                material_external_id={this.props.material_external_id}
                performance_key={this.performance_key ?? ""}
                is_linked_operation={this.props.is_linked_operation}
            >
                {this.renderOrderContent()}
            </DraggableOrder>
        );
    }
}

/**
 * A container component representing a single order displayed in the Gantt chart view.
 * It combines multiple components: OrderRectangle, MaterialTitle (provide them with OrderTooltip), CollectiveOrderBadge... wrapped with DraggableOrder.
 */
const mapState = (state: ReduxState, own_props: OwnProps) => {
    const planning_table = state.gantt_chart_planning_table;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    let line_order;
    for (const line of lines_orders) {
        line_order = line.orders.find(o => o.uuid === own_props.order_uuid);
        if (line_order) {
            break;
        }
    }
    const report_data = state.gantt_chart_report.report_data;
    let from_shift_time: r.ISimulationReportShiftTime | undefined = undefined;
    if (report_data) {
        from_shift_time = report_data.result.from_shift_time;
    }

    const last_clicked_order_uuid = state.gantt_chart_planning_table.last_clicked_order_uuid;
    const material = state.gantt_chart.materials.find(
        material => material.external_id === own_props.material_title_attributes.material_external_id
    );
    const base_order = state.gantt_chart.orders.find(o => o.uuid === own_props.order_uuid);
    const line_group_uuid = state.gantt_chart_properties.line_group_uuid || "";
    const waterfall_mode = state.gantt_chart_properties.waterfall_mode;

    let original_earliest_start: number | undefined;
    let original_earliest_end: number | undefined;
    let original_initial_earliest_start: number | undefined;
    let original_initial_earliest_end: number | undefined;

    const original_lines_orders = state.gantt_chart_lines_orders.original_lines_orders || [];
    const original_unscheduled_orders = state.gantt_chart_lines_orders.original_unscheduled_orders || [];

    for (const line of [...original_lines_orders, ...original_unscheduled_orders]) {
        const order = line.orders.find(o => o.uuid === own_props.order_uuid);
        if (order !== undefined) {
            original_earliest_start = order.earliest_start;
            original_earliest_end = order.earliest_end;
            original_initial_earliest_start = order.initial_earliest_start;
            original_initial_earliest_end = order.initial_earliest_end;
            break;
        }
    }

    const is_multiselected =
        planning_table.multiselect_order_uuids !== null &&
        planning_table.multiselect_order_uuids.includes(own_props.order_uuid);

    return {
        line_order,
        is_rescheduled: last_clicked_order_uuid === own_props.order_uuid,
        is_ctrl_down: planning_table.is_ctrl_down,
        source_type: state.gantt_chart_properties.source_type,
        show_edit_comment: planning_table.show_edit_comment_modal_order_uuid === own_props.order_uuid,
        material,
        base_order,
        line_group_uuid,
        from_shift_time,
        original_earliest_start,
        original_earliest_end,
        original_initial_earliest_start,
        original_initial_earliest_end,
        waterfall_mode,
        is_multiselected,
    };
};

type ReduxProps = ReturnType<typeof mapState>;

export default connect<ReturnType<typeof mapState>, {}, OwnProps, ReduxState>(mapState)(Order);
